import Cookies from 'js-cookie'
import { generateToast } from '@components/toasting'
import { BaseModule } from '@core/base/module/base-module'
import { ROUTES } from '@core/config/routing'
import { AppModule } from '@core/modules/app/app.module'
import { AccountService, ScansService } from '@core/services/api-gateway'
import {
    IAccountInfoResponseData,
    IAccountState,
    IAccountSubscription,
    ICurrentAccount,
    IMdrProduct,
    TAccountServiceUserLoginSuccess,
    TAccountType,
    TPolicyExpirationStatus,
} from './account'

const state = {
    currentAccount: null,
    accounts: {},
    expiredAccountsNames: [],
    policyExpirationStatus: 'valid',
    expirationDate: '',
    countdownInDays: 0,
    nextScanDate: '',
    isMdrActive: false,
    activeMdrProducts: [],
    supportedMdrProducts: [],
    accountType: null,
} satisfies IAccountState

const EXPIRATION_NOTIFICATION_DURATION = 1 * 60 * 1000 // 1 minute

class Account extends BaseModule<IAccountState> {
    constructor(state: IAccountState) {
        super(state, 'account.module')

        this.$eventBus.on(
            'app_initialized',
            async ({ accounts, expiredAccountsNames }) => {
                this.store.setKey('currentAccount', accounts[this.accountId])
                this.store.setKey('accounts', accounts)
                this.store.setKey('expiredAccountsNames', expiredAccountsNames)
                this.validateLicenseExpiration()
            }
        )
    }

    validateLicenseExpiration() {
        const { accountType } = this.store.get()

        const validationByAccountType: Record<TAccountType, () => void> = {
            MDR: () => this.validateMdrExpiration(),
            POLICY: () => this.validatePolicyExpiration(),
        }

        const validate =
            (accountType && validationByAccountType[accountType]) ??
            validationByAccountType.POLICY
        validate()
    }

    private validatePolicyExpiration() {
        const { policyExpirationStatus, expirationDate, countdownInDays } =
            this.store.get()

        if (
            policyExpirationStatus === 'about_to_expire' &&
            countdownInDays > 0
        ) {
            const aboutToExpireCountdownPhrase =
                countdownInDays === 1
                    ? 'in 1 Day'
                    : `in ${countdownInDays} Days`

            generateToast({
                type: 'warning',
                data: `Your Cyber Insurance Policy and Stance Account expire ${aboutToExpireCountdownPhrase}. Policy Expiration Date: ${expirationDate}`,
                autoCloseDuration: EXPIRATION_NOTIFICATION_DURATION,
                testId: 'policy-about-to-expire-toast',
            })
        }
        if (policyExpirationStatus === 'grace_expiration') {
            const graceExpirationPhrase =
                countdownInDays === 1 ? '1 Day' : `${countdownInDays} Days`

            generateToast({
                type: 'error',
                data: `Important Notice: Your Stance Account is expired. ${graceExpirationPhrase} of Grace Period remain`,
                autoCloseDuration: EXPIRATION_NOTIFICATION_DURATION,
                testId: 'policy-grace-expiration-toast',
            })
        }
    }

    // MDR accounts have no Cyber Insurance Policy and no Grace Period
    private validateMdrExpiration() {
        const { policyExpirationStatus, expirationDate, countdownInDays } =
            this.store.get()

        if (
            policyExpirationStatus === 'about_to_expire' &&
            countdownInDays > 0
        ) {
            const aboutToExpireCountdownPhrase =
                countdownInDays === 1
                    ? 'in 1 Day'
                    : `in ${countdownInDays} Days`

            generateToast({
                type: 'warning',
                data: `Your Stance Account expires ${aboutToExpireCountdownPhrase}. Expiration Date: ${expirationDate}`,
                autoCloseDuration: EXPIRATION_NOTIFICATION_DURATION,
                testId: 'mdr-about-to-expire-toast',
            })
        }
    }

    get isMfaEnforced() {
        return this.store.get().currentAccount?.enforce_mfa ?? false
    }

    async setMfaEnforcement(value: boolean) {
        const result = await AccountService.enforceAccountMfa(value)

        if (result) {
            const currentAccountWithUpdatedMfa = {
                ...this.store.get().currentAccount,
                enforce_mfa: value,
            }
            this.store.setKey(
                'currentAccount',
                currentAccountWithUpdatedMfa as ICurrentAccount
            )
            const currentAccountId = currentAccountWithUpdatedMfa.id
            const updatedAccounts = {
                ...this.store.get().accounts,
                [currentAccountId as string]: currentAccountWithUpdatedMfa,
            }
            this.store.setKey(
                'accounts',
                updatedAccounts as Record<string, ICurrentAccount>
            )

            return true
        }
        return false
    }

    async sendNewAccountInvitation(value: string) {
        const result = await AccountService.newAccountInvitation(value)

        return !!result
    }

    async switchAccount(id: string): Promise<void> {
        AppModule.store.setKey('loading', true)
        await this.storage.saveItem('currentAccount', id)
        Cookies.remove('wizerMagicLink')
        window.location.assign(ROUTES.summary.path)
    }

    public async authorizeUser(): Promise<
        TAccountServiceUserLoginSuccess | undefined
    > {
        const result = await AccountService.getAuthUser()
        if (result) {
            return await AccountService.getAllUserAndAccountData()
        }
    }

    private getLatestSubscription = (result: IAccountInfoResponseData) => {
        const latestSubscription = [...result.subscriptions]
            .sort((a: IAccountSubscription, b: IAccountSubscription) => {
                return (
                    new Date(a.expirationDate).getTime() -
                    new Date(b.expirationDate).getTime()
                )
            })
            .at(-1)
        return latestSubscription
    }

    private getAccountType = (
        result: IAccountInfoResponseData
    ): IAccountState['accountType'] => {
        const activeSubscriptions = result.subscriptions.filter(
            (subscription) => subscription.expirationStatus !== 'expired'
        )

        return activeSubscriptions.length === 1 &&
            activeSubscriptions[0].subscriptionType === 'MDR'
            ? 'MDR'
            : 'POLICY'
    }

    private isMdrActive = (
        result: IAccountInfoResponseData
    ): IAccountState['isMdrActive'] => {
        return (
            result.isMdr ||
            result.subscriptions.some(
                (subscription) => subscription.subscriptionType === 'MDR'
            )
        )
    }

    public async getAccountInfo() {
        const result = await AccountService.getAccountInfo()
        if (result) {
            const accountType = this.getAccountType(result)
            const latestSubscription = this.getLatestSubscription(result)

            const latestExpirationDate =
                latestSubscription?.expirationDate ??
                result.policyExpirationDate
            const latestCountdownInDays =
                latestSubscription?.countDownInDays ?? result.countDownInDays

            const isMdrActive = this.isMdrActive(result)

            this.store.setKey(
                'policyExpirationStatus',
                result.policyExpirationStatus as TPolicyExpirationStatus
            )
            this.store.setKey('expirationDate', latestExpirationDate)
            this.store.setKey('countdownInDays', latestCountdownInDays)
            this.store.setKey('isMdrActive', isMdrActive)
            this.store.setKey('accountType', accountType)
            this.store.setKey(
                'activeMdrProducts',
                (result?.mdrProducts || []) as IMdrProduct[]
            )
        }
    }

    public async getMdrSupportedProducts() {
        const result = await AccountService.getMdrSupportedProducts()
        this.store.setKey(
            'supportedMdrProducts',
            (result ?? []) as IMdrProduct[]
        )
    }

    public async getScansInfo() {
        const result = await ScansService.getScansInfo()

        if (result) {
            this.store.setKey('nextScanDate', result.next_scan_date ?? '')
        }
    }

    public async setAccount(account: string) {
        const storedAccountId = (await this.storage.getItem(
            'currentAccount'
        )) as string
        this.accountId = storedAccountId ?? account
    }

    /**
     * Activates account that is passed in query string as aid
     * This happens when user gets an email that he has been added to another account
     */
    public async activateAccountFromEmail(searchParams: TURLSearchParams) {
        // Check if searchParams exist and if it has an 'aid' property
        if (searchParams && searchParams.aid) {
            // Activate the user account using the 'aid' parameter
            const result = await AccountService.activateUser(searchParams.aid)

            // Check if the activation was successful
            if (result) {
                // Save the activated account ID in storage
                // On this.setAccount this value is retrieved from storage to set the current account
                // for all services/modules.
                await this.storage.saveItem('currentAccount', searchParams.aid)

                // Remove the 'aid' property from searchParams
                Reflect.deleteProperty(searchParams, 'aid')

                // Check if searchParams is empty
                if (Object.keys(searchParams).length === 0) {
                    // If empty, remove the searchParams item from storage
                    await this.storage.removeItem('qs')
                } else {
                    // If not empty, save the updated searchParams in storage
                    await this.storage.saveItem('qs', searchParams)
                }
            }
        }
    }

    public getCurrentAccountId() {
        return this.store.get().currentAccount?.id
    }
}

export const AccountModule = new Account(state)
